(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const getCartSurface = () =>
    document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const syncRoutineButtons = async () => {
    const buttons = Array.from(document.querySelectorAll('[data-pc-add-to-cart]'));
    if (!buttons.length) return;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      const variantsInCart = new Set(cart.items.map((item) => String(item.variant_id)));

      buttons.forEach((button) => {
        const isAdded = variantsInCart.has(button.dataset.variantId);
        const state = button.querySelector('[data-pc-add-state]');
        button.classList.toggle('is-added', isAdded);
        button.setAttribute('aria-pressed', String(isAdded));
        if (state && !button.classList.contains('is-loading')) {
          state.textContent = isAdded ? button.dataset.addedLabel : button.dataset.addLabel;
        }
      });
    } catch (error) {
      console.debug('Routine cart sync skipped', error);
    }
  };

  const addRoutineProduct = async (button) => {
    if (button.disabled || button.classList.contains('is-loading') || button.classList.contains('is-added')) return;

    const variantId = Number(button.dataset.variantId);
    const state = button.querySelector('[data-pc-add-state]');
    const cart = getCartSurface();
    const previousLabel = state?.textContent;

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    if (state) state.textContent = button.dataset.addingLabel;

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);

    if (cart?.getSectionsToRender) {
      body.append('sections', cart.getSectionsToRender().map((section) => section.id));
      body.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(button);
    }

    try {
      const response = await fetch(window.routes?.cart_add_url || `${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add product');
      }

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.setAttribute('aria-pressed', 'true');
      button.removeAttribute('aria-busy');
      if (state) state.textContent = button.dataset.addedLabel;

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'peptide-routine',
          productVariantId: String(variantId)
        });
      }

      if (cart?.renderContents && result.sections) {
        cart.renderContents(result);
        cart.classList.remove('is-empty');
      }
    } catch (error) {
      console.error(error);
      button.classList.remove('is-loading');
      button.classList.add('has-error');
      button.removeAttribute('aria-busy');
      if (state) state.textContent = button.dataset.errorLabel;

      window.setTimeout(() => {
        button.classList.remove('has-error');
        if (state) state.textContent = previousLabel || button.dataset.addLabel;
      }, 2600);
    }
  };

  class PeptideRoutineSteps extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.onBlockSelect = (event) => {
        const index = this.tabs.findIndex((tab) => tab.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.select(index);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    select(index) {
      if (!this.tabs[index] || this.tabs[index].getAttribute('aria-selected') === 'true') return;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
        tab.classList.toggle('is-active', isActive);
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });
    }

    onKeydown(event, index) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let nextIndex = index;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      this.select(nextIndex);
      this.tabs[nextIndex].focus();
    }
  }

  if (!customElements.get('peptide-routine-steps')) {
    customElements.define('peptide-routine-steps', PeptideRoutineSteps);
  }

  const setupComparisonCarousels = (root = document) => {
    root.querySelectorAll('[data-pc-comparison-carousel]').forEach((carousel) => {
      if (carousel.dataset.pcReady === 'true') return;
      carousel.dataset.pcReady = 'true';

      carousel.querySelectorAll('[data-pc-comparison-range]').forEach((range) => {
        const comparison = range.closest('[data-pc-comparison]');
        const updatePosition = () => comparison?.style.setProperty('--position', `${range.value}%`);
        range.addEventListener('input', updatePosition);
        updatePosition();
      });

      const track = carousel.querySelector('[data-pc-comparison-track]');
      const previous = carousel.querySelector('[data-pc-carousel-prev]');
      const next = carousel.querySelector('[data-pc-carousel-next]');
      const progress = carousel.querySelector('[data-pc-carousel-progress]');
      if (!track) return;

      const scrollByCard = (direction) => {
        const card = track.querySelector('.pc-compare__card');
        const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
        track.scrollBy({ left: direction * ((card?.getBoundingClientRect().width || track.clientWidth) + gap), behavior: 'smooth' });
      };

      const updateProgress = () => {
        if (!progress) return;
        const maximum = Math.max(1, track.scrollWidth - track.clientWidth);
        const visible = Math.min(1, track.clientWidth / track.scrollWidth);
        const travelled = Math.max(0, Math.min(1, track.scrollLeft / maximum));
        progress.style.width = `${Math.max(14, visible * 100)}%`;
        progress.style.transform = `translateX(${travelled * ((1 / visible) - 1) * 100}%)`;
      };

      previous?.addEventListener('click', () => scrollByCard(-1));
      next?.addEventListener('click', () => scrollByCard(1));
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateProgress), { passive: true });
      window.addEventListener('resize', updateProgress);
      updateProgress();
    });
  };

  const setupScrollZoom = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const images = Array.from(document.querySelectorAll([
      '.peptide-complex-cards main .product__media img',
      '.peptide-complex-cards main .media-with-text__media-wrapper img',
      '.peptide-complex-cards main .image-comparison img',
      '.peptide-complex-cards main .pc-routine-steps__lifestyle img',
      '.peptide-complex-cards main .pc-routine-steps__product-image img',
      '.peptide-complex-cards main .pc-transformation__image img',
      '.peptide-complex-cards main .pc-compare__image img',
      '.peptide-complex-cards main .pc-ingredients__media img',
      '.peptide-complex-cards main .pc-story__media img',
      '.peptide-complex-cards main .pc-test__image img'
    ].join(','))).filter((image) => !image.closest('.pc-routine__card, .product-sticky-cart'));

    images.forEach((image) => image.classList.add('pc-scroll-zoom-image'));
    let frame;

    const update = () => {
      frame = null;
      const viewportHeight = window.innerHeight || 1;
      images.forEach((image) => {
        const rect = image.getBoundingClientRect();
        if (rect.bottom < -viewportHeight || rect.top > viewportHeight * 2) return;
        const progress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / (viewportHeight + rect.height)));
        const scale = 1.045 - progress * 0.065;
        image.style.setProperty('--pc-scroll-scale', scale.toFixed(4));
      });
    };

    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
  };

  ready(() => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-pc-add-to-cart]');
      if (button) addRoutineProduct(button);
    });
    syncRoutineButtons();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => window.setTimeout(syncRoutineButtons, 180));
    }
    setupComparisonCarousels();
    setupScrollZoom();
    document.addEventListener('shopify:section:load', (event) => setupComparisonCarousels(event.target));
  });
})();
